import React, { useEffect, useMemo, useState } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { useParams } from 'react-router-dom';
import { API_URL } from '../Config';
import config from '../common/Config';
import generateOrderSummaryPDF from './generateOrderSummaryPDF';
import generateOrderSummaryPDF_tsc from './generateOrderSummaryPDF_tsc';
import generateOrderSummaryPDF_knm from './generateOrderSummaryPDF_knm';
import generateOrderSummaryPDF_eve from './generateOrderSummaryPDF_eve';
import { fetchOrderHistoryDetails } from '../../utils/orderHistoryApi';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Iconify } from 'src/index';
import '../common/commonStyles.css';

const formatType = value => {
  if (!value) return 'Box';
  const stringValue = String(value);
  return stringValue.charAt(0).toUpperCase() + stringValue.slice(1);
};

const toNumber = value => {
  const numeric = parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const resolveLineUnitPrice = row => {
  const unitPriceValue = toNumber(row.unit_price);
  const originalPriceValue = toNumber(row.original_price);
  const discountPriceValue = toNumber(row.discount_price);
  const originalPrice = originalPriceValue > 0 ? originalPriceValue : unitPriceValue;
  const hasDiscountPriceField = discountPriceValue > 0;
  const discountPriceDifferentFromOriginal = hasDiscountPriceField && discountPriceValue !== originalPrice;
  const discountPriceDifferentFromUnit = hasDiscountPriceField && discountPriceValue !== unitPriceValue;
  const hasValidDiscountPrice = discountPriceDifferentFromOriginal && discountPriceDifferentFromUnit;
  return hasValidDiscountPrice ? discountPriceValue : unitPriceValue;
};

const isFreeProductLine = row => {
  const freeProduct = String(row.free_product ?? '').trim().toUpperCase();
  if (freeProduct === '1' || freeProduct === '(F)' || freeProduct === 'Y') {
    return true;
  }
  const promoType = String(row.promo_type ?? row.buyOrGet ?? row.promoType ?? '').trim().toUpperCase();
  if (promoType === 'FREE') {
    return true;
  }
  if (toNumber(row.discount) === 100) {
    return true;
  }
  return resolveLineUnitPrice(row) === 0;
};

const OrderSummaryPrint = () => {
  const { orderId } = useParams();
  const [shopDetails, setShopDetails] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [productDetails, setProductDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [totalVal, setTotalVal] = useState({
    totalstd: 0,
    totalzeroGoods: 0,
    totalTax: 0,
    orderDiscount: 0,
    orderDiscount1: 0,
    over_all_amount: 0,
    over_all_discount: 0,
    grandTotal: 0,
    totalCess: 0,
    pos: 0,
    promoDiscount: 0,
    promoVat: 0
  });

  const accessToken = localStorage.getItem('accessToken');
  const companyTaxType = localStorage.getItem('company_tax_type') || 'VAT';
  const currencySymbol = localStorage.getItem('currencySymbol') || '\u00a3';
  const showCessRate = localStorage.getItem('showCessRate') === 'Y';
  const token = localStorage.getItem('token');

  const formatCurrency = value => `${currencySymbol}${toNumber(value).toFixed(2)}`;

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setFetchError(false);
      const data = await fetchOrderHistoryDetails(orderId, token);
      const details = data?.details;
      const shop = details?.shop_details;
      const order = details?.orderDetails?.[0];
      const products = details?.product_details || [];
      if (!order) {
        setShopDetails(null);
        setOrderDetails(null);
        setProductDetails([]);
        setTotalVal({
          totalstd: 0,
          totalzeroGoods: 0,
          totalTax: 0,
          orderDiscount: 0,
          orderDiscount1: 0,
          over_all_amount: 0,
          over_all_discount: 0,
          grandTotal: 0,
          totalCess: 0,
          pos: 0,
          promoDiscount: 0,
          promoVat: 0
        });
        return;
      }
      const bundleDiscounts = Array.isArray(details?.bundle_discounts)
        ? details.bundle_discounts
        : [];
      let promoDiscount = 0;
      let promoVat = 0;
      if (bundleDiscounts.length > 0) {
        promoDiscount = bundleDiscounts.reduce(
          (sum, item) => sum + toNumber(item.total_promo_discount),
          0
        );
        promoVat = bundleDiscounts.reduce(
          (sum, item) => sum + toNumber(item.total_promo_discount_vat),
          0
        );
      } else {
        promoDiscount = toNumber(order.total_promo_discount);
        promoVat = toNumber(order.total_promo_discount_vat);
      }
      const chargeableProducts = products.filter(item => !isFreeProductLine(item));
      const totalStdGoods = chargeableProducts
        .filter(item => item.item_tax !== '0.00' && item.item_tax !== null)
        .reduce((sum, item) => {
          if (order.pos === 0 || order.pos === '0') {
            return sum + (toNumber(item.unit_price) * toNumber(item.order_qty) - toNumber(item.item_discount));
          }
          return sum + toNumber(item.subtotal);
        }, 0);
      const totalZeroGoods = chargeableProducts
        .filter(item => item.item_tax === '0.00' || item.item_tax === null)
        .reduce((sum, item) => {
          if (order.pos === 0 || order.pos === '0') {
            return sum + toNumber(item.unit_price) * toNumber(item.order_qty);
          }
          return sum + toNumber(item.subtotal);
        }, 0);
      const totalTax = chargeableProducts.reduce((sum, item) => {
        const itemTax = toNumber(item.item_tax);
        if (itemTax > 0) {
          return sum + itemTax;
        }
        return sum;
      }, 0);
      const totalCess = showCessRate
        ? chargeableProducts
          .filter(item => item.item_tax !== '0.00' && item.item_tax !== null)
          .reduce((sum, item) => {
            const subtotal = toNumber(item.subtotal);
            const rate = toNumber(item.cess_rate_percent);
            return sum + (subtotal * rate) / 100;
          }, 0)
        : 0;
      const orderDiscount = toNumber(order.order_discount);
      const orderDiscount1 = toNumber(order.order_discount1);
      const overAllAmount = toNumber(order.over_all_amount);
      const overAllDiscount = toNumber(order.over_all_discount);
      const discountVat = toNumber(order.discountVat);
      const pos = toNumber(order.pos);
      const vatDiscountFromOverall = totalTax * (overAllDiscount / 100);
      const adjustedTax = totalTax - discountVat - vatDiscountFromOverall;
      const goodsAmount = totalStdGoods + totalZeroGoods;
      const goodsDiscountFromOrderDiscount1 = goodsAmount * (orderDiscount1 / 100);
      const grandTotal =
        goodsAmount + adjustedTax + totalCess - overAllAmount - goodsDiscountFromOrderDiscount1 - promoDiscount - promoVat;
      setShopDetails(shop);
      setOrderDetails(order);
      setProductDetails(products);
      setTotalVal({
        totalstd: totalStdGoods,
        totalzeroGoods: totalZeroGoods,
        totalTax: adjustedTax,
        orderDiscount,
        orderDiscount1,
        over_all_amount: overAllAmount,
        over_all_discount: overAllDiscount,
        grandTotal,
        totalCess,
        pos,
        promoDiscount,
        promoVat
      });
    } catch (error) {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchInvoice();
  }, [accessToken, orderId]);

  const filteredProducts = useMemo(
    () => productDetails.filter(item => toNumber(item.order_qty) > 0),
    [productDetails]
  );

  const invoiceTotalValue = useMemo(() => {
    const goodsAmount = totalVal.totalstd + totalVal.totalzeroGoods;
    const goodsDiscountFromOrderDiscount1 = goodsAmount * ((totalVal.orderDiscount1 || 0) / 100);
    const total =
      goodsAmount +
      totalVal.totalTax +
      totalVal.totalCess -
      totalVal.over_all_amount -
      goodsDiscountFromOrderDiscount1 -
      totalVal.promoDiscount -
      totalVal.promoVat;
    return Math.max(0, Math.round(total * 100) / 100);
  }, [totalVal]);

  const invoiceData = useMemo(() => {
    if (!orderDetails) {
      return null;
    }
    const promoVat = totalVal.promoVat;
    const promoDiscount = totalVal.promoDiscount;
    return {
      promoVat,
      promoDiscount,
      shopDetails: shopDetails || {},
      customerName: orderDetails.company || orderDetails.name || '',
      address: [orderDetails.address, orderDetails.city, orderDetails.postal_code, orderDetails.country]
        .filter(Boolean)
        .join(', ')
        .replace(/^,\s*|,\s*$/g, ''),
      deliveryAddress: orderDetails.delivery_address || '',
      accountNo: orderDetails.account_no || '',
      tel: orderDetails.phone || '-',
      email: orderDetails.email || '-',
      orderDate: orderDetails.date || '',
      orderNumber: orderDetails.reference_no || '',
      saleStatus: orderDetails.sale_status || '-',
      routeNumber: orderDetails.route_number || '-',
      paymentStatus: orderDetails.payment_status || '-',
      products: filteredProducts.map((row, index) => {
        const unitPriceValue = toNumber(row.unit_price);
        const originalPriceValue = toNumber(row.original_price);
        const discountPriceValue = toNumber(row.discount_price);
        const discount = toNumber(row.item_discount);
        const originalPrice = originalPriceValue > 0 ? originalPriceValue : unitPriceValue;
        const hasDiscountPriceField = discountPriceValue > 0;
        const discountPriceDifferentFromOriginal = hasDiscountPriceField && discountPriceValue !== originalPrice;
        const discountPriceDifferentFromUnit = hasDiscountPriceField && discountPriceValue !== unitPriceValue;
        const hasValidDiscountPrice = discountPriceDifferentFromOriginal && discountPriceDifferentFromUnit;
        const unitPrice = hasValidDiscountPrice ? discountPriceValue : unitPriceValue;
        const vatAmount = toNumber(row.item_tax);
        const netAmount = toNumber(row.subtotal);
        const qty = toNumber(row.order_qty);
        const isFree = isFreeProductLine(row);
        const hasActualDiscount = hasValidDiscountPrice || discount > 0;
        const isDiscounted = !isFree && hasActualDiscount;
        const cessRate = toNumber(row.cess_rate_percent);
        const taxRate = toNumber(row.tax_rate);
        const productCode = row.code || row.product_code || '';
        const productName = row.product_name || '';
        const productSize = row.size || '';
        let description = productName;
        if (productCode) {
          description = `${productCode} - ${description}`;
        }
        if (productSize) {
          description = `${description} - ${productSize}`;
        }
        return {
          id: row.id || row.product_code || `row-${index}`,
          description: description || '',
          qty,
          type: formatType(row.order_type || 'box'),
          unitPrice,
          originalPrice: hasActualDiscount ? originalPrice : unitPrice,
          discount,
          vatAmount,
          taxRate,
          netAmount,
          cessAmount: (netAmount * cessRate) / 100,
          isFree,
          isDiscounted
        };
      }),
      totals: {
        totalStdGoods: totalVal.totalstd,
        vatAmount: totalVal.totalTax,
        totalZeroGoods: totalVal.totalzeroGoods,
        promotionalDiscount: totalVal.orderDiscount,
        totalInvDisc: totalVal.over_all_amount,
        invoiceDiscountPercent: totalVal.over_all_discount,
        orderDiscountPercent: totalVal.orderDiscount1,
        invoiceTotal: invoiceTotalValue,
        cessAmount: totalVal.totalCess
      }
    };
  }, [orderDetails, shopDetails, filteredProducts, totalVal, invoiceTotalValue]);

  const handleGenerateDeliveryNotePDF = () => {
    if (accessToken === 'tsc' || accessToken === 'tss') {
      generateOrderSummaryPDF_tsc(orderId);
    } else if (accessToken === 'knm') {
      generateOrderSummaryPDF_knm(orderId);
    } else if (accessToken === 'eve' || accessToken === 've') {
      generateOrderSummaryPDF_eve(orderId);
    } else {
      generateOrderSummaryPDF(orderId);
    }
  };

  const showPrintButton = accessToken === 'ayt' || accessToken === 'eve' || totalVal.pos === 1;
  const grossTotal = totalVal.totalstd + totalVal.totalzeroGoods + totalVal.totalTax + totalVal.totalCess;

  if (loading) {
    return (
      <div>
        <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', my: 6 }}>
          <Box sx={{ width: '100%', px: { xs: 2, md: 3 }, py: 2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid rgba(145, 158, 171, 0.16)',
                overflow: 'hidden',
                backgroundColor: '#fff',
                p: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
              }}
            >
              <CircularProgress />
            </Paper>
          </Box>
        </Box>
      </div>
    );
  }

  if (!invoiceData || fetchError) {
    return (
      <>
        <Box sx={{ width: '100%', p: 1.5, backgroundColor: '#f5f7fa' }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(145, 158, 171, 0.16)',
              overflow: 'hidden',
              backgroundColor: '#fff',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              gap: 2
            }}
          >
            <Iconify icon="solar:clipboard-remove-bold" width={120} sx={{ color: '#637381' }} />
            <Typography variant="h5" sx={{ color: '#212b36', fontWeight: 600 }}>
              Invoice Not found
            </Typography>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography className="page-title">
                  Order Summary
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 2 }}>
                {showPrintButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Iconify icon="solar:printer-bold" width={16} />}
                    onClick={handleGenerateDeliveryNotePDF}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 'none',
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': {
                        boxShadow: 'none'
                      }
                    }}
                  >
                    Print Invoice
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ px: 2, pb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Customer Name:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.customerName}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Address:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.deliveryAddress || invoiceData.address}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Customer a/c No.</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.accountNo}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Tel:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.tel}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Email:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.email}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Order Date:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.orderDate}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Order:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.orderNumber}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Sale Status:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.saleStatus}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Route Number:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.routeNumber}</Typography>
                  </Box>
                  <Box className="innvoice-row-item">
                    <Typography className="innvoice-title">Payment Status:</Typography>
                    <Typography className="innvoice-subtitle">{invoiceData.paymentStatus}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <TableContainer>
            <Table className="table-cell-border">
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell sx={{ width: '300px' }}>Product Description</TableCell>
                  <TableCell align="center" sx={{ width: '50px' }}>Qty</TableCell>
                  <TableCell align="center" sx={{ width: '50px' }}>Type</TableCell>
                  <TableCell align="right" sx={{ width: '70px' }}>Unit Price</TableCell>
                  <TableCell align="right" sx={{ width: '70px' }}>Discount</TableCell>
                  <TableCell align="right" sx={{ width: '70px' }}>{companyTaxType} Amount</TableCell>
                  {showCessRate && (
                    <TableCell align="right" sx={{ width: '50px' }}>Cess Amount</TableCell>
                  )}
                  <TableCell align="right" sx={{ width: '70px' }}>Net Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceData.products.length > 0 ? (
                  invoiceData.products.map(product => (
                    <TableRow
                      key={product.id}
                      className="table-cell-style"
                    >
                      <TableCell>
                        <Typography>{product.description}</Typography>
                        {product.isFree && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                            <Iconify icon="solar:circle-bold" width={4} sx={{ color: '#003768', mr: 0.25 }} />
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.25} sx={{ color: '#003768', fontSize: '0.65rem' }}>
                              <Iconify icon="solar:check-circle-bold" width={10} />
                              (F)
                            </Typography>
                          </Box>
                        )}
                        {product.isDiscounted && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                            <Iconify icon="solar:circle-bold" width={4} sx={{ color: '#003768', mr: 0.25 }} />
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.25} sx={{ color: '#003768', fontSize: '0.65rem' }}>
                              <Iconify icon="solar:check-circle-bold" width={10} />
                                (P)
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{product.qty}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>{product.type}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        {product.isFree ? (
                          <Box>
                            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#637381', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              {formatCurrency(product.originalPrice)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: '#212b36' }}>
                              Free
                            </Typography>
                          </Box>
                        ) : product.isDiscounted ? (
                          <Box>
                            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#637381', display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              {formatCurrency(product.originalPrice)}
                            </Typography>
                            <Typography variant="body2" sx={{ display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: '#212b36' }}>
                              {formatCurrency(product.unitPrice)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: '#212b36' }}>
                            {formatCurrency(product.unitPrice)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          {product.isFree ? '-' : formatCurrency(product.discount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          {product.isFree ? '-' : formatCurrency(product.vatAmount)}
                        </Typography>
                      </TableCell>
                      {showCessRate && (
                        <TableCell align="right">
                          <Typography>
                            {product.isFree ? '-' : formatCurrency(product.cessAmount)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Typography>
                          {product.isFree ? '-' : formatCurrency(product.netAmount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={showCessRate ? 8 : 7} align="center" sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: '#637381', fontSize: '0.6rem' }}>No products found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={2} px={2} mt={1}>
            <Grid size={{ xs: 12, md: 6 }} />
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 0.5 } }}>
                <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">Total VAT Goods</Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(invoiceData.totals.totalStdGoods)}
                  </Typography>
                </Box>
                <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">{companyTaxType} Amount</Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(invoiceData.totals.vatAmount)}
                  </Typography>
                </Box>
                {showCessRate && (
                  <Box className="innvoice-summary-align">
                    <Typography className="innvoice-summary-title">Cess Amount</Typography>
                    <Typography className="innvoice-summary-value" >
                      {formatCurrency(invoiceData.totals.cessAmount)}
                    </Typography>
                  </Box>
                )}
                <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">Total Zero VAT Goods</Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(invoiceData.totals.totalZeroGoods)}
                  </Typography>
                </Box>
                <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">Gross Total</Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(grossTotal)}
                  </Typography>
                </Box>
                {/* <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 600, color: '#212b36', fontSize: '0.8rem' }}>Promotional Discount</Typography>
                  <Typography sx={{ color: '#212b36', fontSize: '0.8rem', minWidth: { xs: 100, sm: 80 }, textAlign: 'right' }}>
                    {formatCurrency(invoiceData.totals.promotionalDiscount)}
                  </Typography>
                </Box> */}
                              <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">Promo Discount</Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(invoiceData.promoDiscount)}
                  </Typography>
                </Box>
                <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">Promo Vat</Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(invoiceData.promoVat)}
                  </Typography>
                </Box>
  

                {invoiceData.totals.orderDiscountPercent > 0 && (
                  <Box className="innvoice-summary-align">
                    <Typography className="innvoice-summary-title">Order Discount</Typography>
                    <Typography className="innvoice-summary-value" >
                      {invoiceData.totals.orderDiscountPercent.toFixed(2)}%
                    </Typography>
                  </Box>
                )}
                {(invoiceData.totals.totalInvDisc > 0 || invoiceData.totals.invoiceDiscountPercent > 0) && (
                  <Box className="innvoice-summary-align">
                    <Typography className="innvoice-summary-title">Invoice Discount</Typography>
                    <Typography className="innvoice-summary-value">
                      {invoiceData.totals.invoiceDiscountPercent > 0
                        ? `${invoiceData.totals.invoiceDiscountPercent.toFixed(2)}% (${formatCurrency(invoiceData.totals.totalInvDisc)})`
                        : formatCurrency(invoiceData.totals.totalInvDisc)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: { xs: 0.75, sm: 0.5 }, borderColor: 'rgba(145, 158, 171, 0.16)' }} />
                <Box className="innvoice-summary-align">
                  <Typography className="innvoice-summary-title">
                    Invoice Total
                  </Typography>
                  <Typography className="innvoice-summary-value" >
                    {formatCurrency(invoiceData.totals.invoiceTotal)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box className="invoice-footer-section">
            <Typography className="invoice-footer-text">
              No claims for damages or short delivery will be considered unless made at the time of delivery of goods. These goods are not sold on a sale or return basis.
            </Typography>
          </Box>

        </Paper>
      </Box>
    </>
  );
};

export default OrderSummaryPrint;

